import { SCOrderListResponseType } from "@sellar-io/api-v2/types";
import { OnChangeFn, PaginationState } from "@tanstack/react-table";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import { formatCurrency } from "@sellar-io/common";
import {
  getOrderStage,
  getOrderStageBadge,
  Icon,
  ProfilePicture,
  SpinnerLegacy,
} from "../../../../../../components";
import { useSellarCreditOrderStore } from "../../../../stores/SellarCreditOrderStore";
import { useShallow } from "zustand/shallow";
import { SellarCreditOrderSortType } from "../../../../stores/SellarCreditTabTypes";
import { UseQueryResult } from "@tanstack/react-query";
import { TabPaymentStatusBadge } from "./TabPaymentStatusBadge";
import {
  DataButton,
  TablePaginated,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { useGetTwoPaymentLink } from "../hooks/useGetTwoPaymentLink";
import { canOrderBePaid } from "../utils/orderPaymentStatus";

type ColumnDataType = SCOrderListResponseType[number];

export const SellarCreditOrderTable = ({
  query,
  handleTableSort,
  pagination,
  setPagination,
  setSelectedOrder,
  setActive,
  totalResults,
}: {
  query: UseQueryResult<SCOrderListResponseType>;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  handleTableSort: (value: SellarCreditOrderSortType | null) => void;
  setSelectedOrder: (order: SCOrderListResponseType[number]) => void;
  setActive: (value: boolean) => void;
  totalResults: number;
}) => {
  const { columnSort } = useSellarCreditOrderStore(
    useShallow((state) => ({
      columnSort: state.columnSort,
    })),
  );

  const getPaymentLinkMutation = useGetTwoPaymentLink();

  const { data } = query;
  const flatData = useMemo(() => data?.map((row) => row) ?? [], [data]);

  const pageCount = Math.ceil(totalResults / pagination.pageSize);

  const columnHelper = createSellarColumnHelper<ColumnDataType>();

  const columns = [
    columnHelper.accessor("id", {
      header: "",
      enableSorting: false,
      cell: () => {
        return <></>; //Empty column to help with UI padding
      },
    }),
    columnHelper.accessor("id", {
      id: "orderId",
      header: "Order #",
      enableSorting: true,
      cell: (info) => {
        const orderId = info.getValue() || "NA";
        const supplier = info.row.original.Supplier;
        return (
          <div className="flex items-center gap-2 px-4 pl-0">
            <ProfilePicture
              size="small"
              logoUrl={supplier.logoUrl}
              fallback="supplier-badge"
            />
            <div className="flex w-full flex-col">
              <span className="text-sm font-medium text-zinc-800">
                {supplier.name}
              </span>
              <span className="text-xs text-zinc-500">{`Order #${orderId}`}</span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("Business", {
      id: "business",
      header: "Business",
      enableSorting: true,
      cell: (info) => {
        const business = info.getValue();
        return business.name;
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "user",
      header: "User",
      enableSorting: false,
      cell: (info) => {
        const userName = info.getValue().orderPlacedByName ?? "";
        return userName;
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "orderStatus",
      header: "Order status",
      enableSorting: false,
      cell: (info) => {
        const order = info.getValue();
        const stage = getOrderStage(order);
        return getOrderStageBadge(stage);
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "paymentStatus",
      header: "Payment status",
      enableSorting: false,
      cell: (info) => {
        const order = info.getValue();
        return <TabPaymentStatusBadge order={order} />;
      },
    }),
    columnHelper.accessor("price", {
      id: "orderTotal",
      header: "Order total",
      enableSorting: false,
      cell: (info) => {
        const total = info.getValue();
        return formatCurrency(total);
      },
    }),
    columnHelper.accessor("createdAt", {
      id: "datePlaced",
      header: "Date placed",
      enableSorting: false,
      cell: (info) => {
        const createdAt = info.getValue();
        return dayjs(createdAt).format("DD/MM/YYYY");
      },
    }),

    columnHelper.accessor((row) => row, {
      id: "payAction",
      header: "",
      enableSorting: false,
      cell: (row) => {
        const order = row.getValue();

        const orderCanBePaid = canOrderBePaid(order);

        if (orderCanBePaid) {
          return (
            <div
              className="mx-auto"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <DataButton
                disabled={getPaymentLinkMutation.isPending}
                border
                onClick={() => {
                  const orderId = order.id;
                  getPaymentLinkMutation
                    .mutateAsync(orderId)
                    .then((result) => {
                      window.open(result.link, "_blank");
                    })
                    .catch((error) => {
                      console.error(error);
                    });
                }}
              >
                Pay
              </DataButton>
            </div>
          );
        }

        return null;
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "action",
      header: "",
      enableSorting: false,
      cell: () => (
        <div className="flex w-full justify-end">
          <Icon icon="CHEVRON_RIGHT" size={1} />
        </div>
      ),
    }),
  ];

  if (query.isLoading && flatData.length === 0) {
    return <SpinnerLegacy />;
  }

  return (
    <div className={twMerge("max-h-[calc(100vh-250px)] border-zinc-300")}>
      <TablePaginated
        columns={columns}
        data={flatData}
        pageCount={pageCount}
        pagination={pagination}
        setPagination={setPagination}
        columnSortName={columnSort}
        handleTableSort={handleTableSort}
        onRowClick={(row) => {
          setSelectedOrder(row);
          setActive(true);
        }}
        noResultsText="Your search returned 0 results"
        paginationLabel="orders per page"
        gridClasses="grid-cols-[16px_minmax(200px,_1fr)_minmax(200px,_1fr)_1fr_1fr_140px_minmax(90px,_1fr)_minmax(60px,_1fr)_80px_40px]"
      />
    </div>
  );
};
